"use client";

import { useState, useEffect } from "react";
import { usePreferences } from "../contexts/PreferencesContext";
import { useTranslation } from "react-i18next";
import {
  supportedCurrencies,
  supportedLanguages,
  CurrencyCode,
  LanguageCode,
} from "@/constants/localization";

export default function Footer() {
  const { currency, language, setCurrency, setLanguage } = usePreferences();
  const [mounted, setMounted] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    setMounted(true);
  }, []);

  // Show loading skeleton while mounting
  if (!mounted) {
    return (
      <footer className="w-full bg-gradient-to-b from-slate-800/50 to-slate-900/50 backdrop-blur-sm border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-32 h-8 bg-white/10 rounded-lg animate-pulse" />
                <div className="w-40 h-10 bg-white/10 rounded-lg animate-pulse" />
              </div>
              <div className="flex items-center gap-2">
                <div className="w-32 h-8 bg-white/10 rounded-lg animate-pulse" />
                <div className="w-40 h-10 bg-white/10 rounded-lg animate-pulse" />
              </div>
            </div>
            <div className="w-48 h-6 bg-white/10 rounded-lg animate-pulse" />
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="w-full bg-gradient-to-b from-slate-800/50 to-slate-900/50 backdrop-blur-sm border-t border-white/10">
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-4">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 sm:gap-4">
          {/* Currency and Language Selectors */}
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              <label
                htmlFor="currency"
                className="block text-m font-medium text-white/60 mb-1 sm:mb-0 w-full sm:w-32 text-center sm:text-right"
              >
                {t("footer.currency")}
              </label>
              <div className="relative group w-full sm:w-auto">
                <select
                  id="currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as CurrencyCode)}
                  className="w-full sm:w-auto pr-8 bg-white/10 border border-white/20 text-white rounded-lg px-4 py-2.5 sm:py-2 appearance-none cursor-pointer hover:bg-white/20 transition-colors text-center sm:text-left"
                >
                  {supportedCurrencies.map((c) => (
                    <option
                      key={c.code}
                      value={c.code}
                      className="bg-slate-800"
                    >
                      {`${c.symbol} ${c.code} - ${t(
                        `footer.currencies.${c.code}` as any
                      )}`}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-white/60">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              <label
                htmlFor="language"
                className="block text-m font-medium text-white/60 mb-1 sm:mb-0 w-full sm:w-32 text-center sm:text-right"
              >
                {t("footer.language")}
              </label>
              <div className="relative group w-full sm:w-auto">
                <select
                  id="language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as LanguageCode)}
                  className="w-full sm:w-auto pr-8 bg-white/10 border border-white/20 text-white rounded-lg px-4 py-2.5 sm:py-2 appearance-none cursor-pointer hover:bg-white/20 transition-colors text-center sm:text-left"
                >
                  {supportedLanguages.map((l) => (
                    <option
                      key={l.code}
                      value={l.code}
                      className="bg-slate-800"
                    >
                      {l.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-white/60">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="text-white/60 text-sm text-center sm:text-left w-full sm:w-auto">
            © {new Date().getFullYear()} {t("footer.copyright")}
          </div>
        </div>
      </div>
    </footer>
  );
}
